import { createContext, useState, useEffect, useCallback, useMemo, useContext } from "react";
import useAuth from "../../hooks/useAuth";
import { loadEncrypted, saveEncrypted } from "../../utils/encryptedStorage";
import { CustomerState, CustomerContextType, CustomerProviderProps, Customer, Address } from "./customerType";

const STORAGE_KEY = 'checkout_customer';
const PLUGINSPACE_STORAGE_KEY = 'pluginspace_context_guard';
const PLUGINSPACE_CHANGE_EVENT = 'pluginspace:change';


const DEFAULT_STATE: CustomerState = { customer: null, customerAddress: null, addresses: [] };

// ============ CONTEXT ============

export const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

// ============ PROVIDER ============

export function CustomerProvider({ children }: CustomerProviderProps) {
    const { currentPluginspaceId } = useAuth();
    const [state, setState] = useState<CustomerState>(() => {
        // Carregar do localStorage na inicialização
        const saved = loadEncrypted<CustomerState>(STORAGE_KEY);
        return saved || DEFAULT_STATE;
    });

    useEffect(() => {
        if (!currentPluginspaceId) return;
        const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
        const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
        const currentId = String(currentPluginspaceId);

        if (storedPluginspaceId && storedPluginspaceId !== currentId) {
            setState(DEFAULT_STATE);
        }

        if (storedPluginspaceId !== currentId) {
            saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: currentPluginspaceId });
        }
    }, [currentPluginspaceId]);

    // ============ PERSISTÊNCIA ============

    useEffect(() => {
        // Salvar no localStorage sempre que o estado mudar
        saveEncrypted(STORAGE_KEY, state);
    }, [state]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handlePluginspaceChange = (event: Event) => {
            const customEvent = event as CustomEvent<{ pluginspaceId: number }>;
            const newPluginspaceId = customEvent.detail?.pluginspaceId;
            if (!newPluginspaceId) return;

            const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
            const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
            const nextId = String(newPluginspaceId);

            if (storedPluginspaceId && storedPluginspaceId !== nextId) {
                setState(DEFAULT_STATE);
            }

            if (storedPluginspaceId !== nextId) {
                saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: newPluginspaceId });
            }
        };

        window.addEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
        return () => window.removeEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
    }, []);

    // ============ AÇÕES - CUSTOMER (MEMOIZADAS) ============

    const setCustomer = useCallback((customer: Customer) => {
        setState(prev => ({ ...prev, customer }));
    }, []);

    const updateCustomer = useCallback((updates: Partial<Customer>) => {
        setState(prev => ({
            ...prev,
            customer: prev.customer ? { ...prev.customer, ...updates } : null,
        }));
    }, []);

    const clearCustomer = useCallback(() => {
        setState(prev => ({ ...prev, customer: null }));
    }, []);

    // ============ AÇÕES - ADDRESS (MEMOIZADAS) ============

    const setCustomerAddress = useCallback((address: Address) => {
        setState(prev => ({ ...prev, customerAddress: address }));
    }, []);

    const updateCustomerAddress = useCallback((updates: Partial<Address>) => {
        setState(prev => ({
            ...prev,
            customerAddress: prev.customerAddress
                ? { ...prev.customerAddress, ...updates }
                : null,
        }));
    }, []);

    const clearCustomerAddress = useCallback(() => {
        setState(prev => ({ ...prev, customerAddress: null }));
    }, []);

    // ============ AÇÕES - ADDRESSES LIST (MEMOIZADAS) ============

    const addAddress = useCallback((address: Address) => {
        setState(prev => ({
            ...prev,
            addresses: [...prev.addresses, address],
        }));
    }, []);

    const updateAddress = useCallback((addressId: string, updates: Partial<Address>) => {
        setState(prev => ({
            ...prev,
            addresses: prev.addresses.map(addr =>
                addr.id === addressId ? { ...addr, ...updates } : addr
            ),
        }));
    }, []);

    const removeAddress = useCallback((addressId: string) => {
        setState(prev => ({
            ...prev,
            addresses: prev.addresses.filter(addr => addr.id !== addressId),
        }));
    }, []);

    const setAddresses = useCallback((addresses: Address[]) => {
        setState(prev => ({ ...prev, addresses }));
    }, []);

    // ============ QUERIES (MEMOIZADAS) ============

    const isCustomerComplete = useCallback(() => {
        if (!state.customer) return false;
        const { name, email, phone } = state.customer;
        return !!(name && email && phone);
    }, [state.customer]);

    const isAddressComplete = useCallback(() => {
        if (!state.customerAddress) return false;
        const { cep, street, number, neighborhood, city, state: uf } = state.customerAddress;
        return !!(cep && street && number && neighborhood && city && uf);
    }, [state.customerAddress]);

    const getAddress = useCallback((addressId: string) => {
        return state.addresses.find(addr => addr.id === addressId);
    }, [state.addresses]);

    // ============ VALUE (MEMOIZADO) ============

    const value = useMemo<CustomerContextType>(() => ({
        // Estado
        customer: state.customer,
        customerAddress: state.customerAddress,
        addresses: state.addresses,

        // Ações - Customer
        setCustomer,
        updateCustomer,
        clearCustomer,

        // Ações - Address
        setCustomerAddress,
        updateCustomerAddress,
        clearCustomerAddress,

        // Ações - Addresses List
        addAddress,
        updateAddress,
        removeAddress,
        setAddresses,

        // Queries
        isCustomerComplete,
        isAddressComplete,
        getAddress,
    }), [
        state.customer,
        state.customerAddress,
        state.addresses,
        setCustomer,
        updateCustomer,
        clearCustomer,
        setCustomerAddress,
        updateCustomerAddress,
        clearCustomerAddress,
        addAddress,
        updateAddress,
        removeAddress,
        setAddresses,
        isCustomerComplete,
        isAddressComplete,
        getAddress,
    ]);

    return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}


/**
 * Hook principal - retorna todo o contexto
 */
export function useCustomer() {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within CustomerProvider');
  }
  return context;
}

/**
 * Hook seletor - apenas dados do cliente
 */
export function useCustomerData() {
  const { customer } = useCustomer();
  return useMemo(() => customer, [customer]);
}

/**
 * Hook seletor - apenas endereço atual
 */
export function useCustomerAddress() {
  const { customerAddress } = useCustomer();
  return useMemo(() => customerAddress, [customerAddress]);
}

/**
 * Hook seletor - apenas lista de endereços
 */
export function useCustomerAddresses() {
  const { addresses } = useCustomer();
  return useMemo(() => addresses, [addresses]);
}

/**
 * Hook seletor - apenas ações
 * NUNCA re-renderiza
 */
export function useCustomerActions() {
  const {
    setCustomer,
    updateCustomer,
    clearCustomer,
    setCustomerAddress,
    updateCustomerAddress,
    clearCustomerAddress,
    addAddress,
    updateAddress,
    removeAddress,
    setAddresses,
  } = useCustomer();

  return useMemo(() => ({
    setCustomer,
    updateCustomer,
    clearCustomer,
    setCustomerAddress,
    updateCustomerAddress,
    clearCustomerAddress,
    addAddress,
    updateAddress,
    removeAddress,
    setAddresses,
  }), [
    setCustomer,
    updateCustomer,
    clearCustomer,
    setCustomerAddress,
    updateCustomerAddress,
    clearCustomerAddress,
    addAddress,
    updateAddress,
    removeAddress,
    setAddresses,
  ]);
}

/**
 * Hook seletor - validações
 */
export function useCustomerValidation() {
  const { isCustomerComplete, isAddressComplete } = useCustomer();
  
  return useMemo(() => ({
    isCustomerComplete: isCustomerComplete(),
    isAddressComplete: isAddressComplete(),
  }), [isCustomerComplete, isAddressComplete]);
}